// components/reports/StaffReport.jsx
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { CustomBarChart } from '../../components/charts/BarChart';
import { Badge } from '../../../../components/ui/badge';
import { Users, UserCheck, Package } from 'lucide-react';

export function StaffReport({ data }) {
  if (!data) return null;

  const waiterData = data.Table12?.map(waiter => ({
    name: waiter.WaiterName,
    value: waiter.NetSales,
    orders: waiter.OrderCount
  })) || [];

  const riderData = data.Table13
    ?.filter(rider => rider.NetSales > 0)
    .map(rider => ({
      name: rider.RiderName,
      value: rider.NetSales,
      orders: rider.OrderCount
    })) || [];

  // Counter/Terminal data (simulated from order source)
  const counterData = data.Table3
    ?.filter(source => source.OrderSource === 'POS')
    .map(source => ({
      name: 'POS Counter',
      value: source.NetSales,
      orders: source.OrderCount
    })) || [];

  const staffPerformance = [...waiterData, ...riderData];

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Waiters</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{waiterData.length}</div>
            <p className="text-xs text-muted-foreground">
              Active waiters
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Riders</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{riderData.length}</div>
            <p className="text-xs text-muted-foreground">
              Active delivery riders
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">POS Counters</CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{counterData.length}</div>
            <p className="text-xs text-muted-foreground">
              Active counters
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Waiter Performance */}
        <Card>
          <CardHeader>
            <CardTitle>Waiter Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {waiterData.length > 0 ? (
                <CustomBarChart
                  data={waiterData.map(w => ({ name: w.name, value: w.value }))}
                  title=""
                  orientation="vertical"
                  color="#8b5cf6"
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-muted-foreground">No waiter data available</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Rider Performance */}
        <Card>
          <CardHeader>
            <CardTitle>Rider Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {riderData.length > 0 ? (
                <CustomBarChart
                  data={riderData.map(r => ({ name: r.name, value: r.value }))}
                  title=""
                  orientation="vertical"
                  color="#10b981"
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-muted-foreground">No rider data available</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Staff Details */}
      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Staff Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Waiters */}
              {waiterData.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Users className="h-5 w-5" /> Waiters
                  </h3>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {waiterData.map((waiter, index) => (
                      <Card key={index}>
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <p className="font-medium">{waiter.name}</p>
                              <Badge variant="outline" className="mt-1">Waiter</Badge>
                            </div>
                            <Badge className="bg-purple-100 text-purple-800">
                              #{index + 1}
                            </Badge>
                          </div>
                          <div className="space-y-3">
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">Orders</span>
                              <span className="font-semibold">{waiter.orders}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">Sales</span>
                              <span className="font-semibold">{waiter.value?.toLocaleString()}</span>
                            </div>
                            <div className="pt-3 border-t">
                              <div className="text-sm text-muted-foreground">
                                Performance Score
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                                <div 
                                  className="bg-purple-600 h-2 rounded-full"
                                  style={{ width: `${Math.min((waiter.value / 10000) * 100, 100)}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Riders */}
              {riderData.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Package className="h-5 w-5" /> Riders
                  </h3>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {riderData.map((rider, index) => (
                      <Card key={index}>
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <p className="font-medium">{rider.name}</p>
                              <Badge variant="outline" className="mt-1">Rider</Badge>
                            </div>
                            <Badge className="bg-green-100 text-green-800">
                              #{index + 1}
                            </Badge>
                          </div>
                          <div className="space-y-3">
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">Deliveries</span>
                              <span className="font-semibold">{rider.orders}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">Sales</span>
                              <span className="font-semibold">{rider.value?.toLocaleString()}</span>
                            </div>
                            <div className="pt-3 border-t">
                              <div className="text-sm text-muted-foreground">
                                Delivery Score
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                                <div 
                                  className="bg-green-600 h-2 rounded-full"
                                  style={{ width: `${Math.min((rider.value / 5000) * 100, 100)}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Counter/Terminal Performance */}
              {counterData.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <UserCheck className="h-5 w-5" /> Counter Performance
                  </h3>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {counterData.map((counter, index) => (
                      <Card key={index}>
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <p className="font-medium">{counter.name}</p>
                              <Badge variant="outline" className="mt-1">Terminal</Badge>
                            </div>
                            <Badge className="bg-blue-100 text-blue-800">
                              POS
                            </Badge>
                          </div>
                          <div className="space-y-3">
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">Transactions</span>
                              <span className="font-semibold">{counter.orders}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">Sales</span>
                              <span className="font-semibold">{counter.value?.toLocaleString()}</span>
                            </div>
                            <div className="pt-3 border-t">
                              <div className="text-sm text-muted-foreground">
                                Efficiency Score
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                                <div 
                                  className="bg-blue-600 h-2 rounded-full"
                                  style={{ width: `${Math.min((counter.orders / 50) * 100, 100)}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}